import { base58Encode, bytesToHex, type WalletChain } from "./wallet.ts";

/**
 * Browser wallets, with no SDK: EVM wallets through EIP-1193 (found with EIP-6963 announcements,
 * else `window.ethereum`) and Solana wallets through the `connect` / `signMessage` provider that
 * Phantom, Solflare and Backpack inject. Only two calls are ever made: read the address and sign
 * a plain-text message. No transaction is ever built.
 */

interface Eip1193 {
  request(args: { method: string; params?: unknown[] }): Promise<unknown>;
}

interface SolanaProvider {
  connect(): Promise<{ publicKey: { toBase58(): string } } | void>;
  publicKey?: { toBase58(): string } | null;
  signMessage(msg: Uint8Array, encoding?: string): Promise<{ signature: Uint8Array } | Uint8Array>;
}

type W = Window & {
  ethereum?: Eip1193;
  solana?: SolanaProvider & { isPhantom?: boolean };
  phantom?: { solana?: SolanaProvider };
  solflare?: SolanaProvider;
  backpack?: { solana?: SolanaProvider };
};

const announced: Eip1193[] = [];
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("eip6963:announceProvider", (e) => {
    const p = (e as CustomEvent<{ provider?: Eip1193 }>).detail?.provider;
    if (p && !announced.includes(p)) announced.push(p);
  });
  window.dispatchEvent(new Event("eip6963:requestProvider"));
}

function win(): W | null {
  return typeof window !== "undefined" ? (window as W) : null;
}

function evmProvider(): Eip1193 | null {
  return announced[0] ?? win()?.ethereum ?? null;
}

function solanaProvider(): SolanaProvider | null {
  const w = win();
  return w?.phantom?.solana ?? w?.solflare ?? w?.backpack?.solana ?? w?.solana ?? null;
}

export function hasWallet(chain: WalletChain): boolean {
  return chain === "evm" ? !!evmProvider() : !!solanaProvider();
}

/** A phone without a wallet in this browser (Safari, Chrome): the wallet app's own browser is the way. */
export function needsWalletApp(): boolean {
  const w = win();
  return !!w && /Android|iPhone|iPad|iPod/i.test(w.navigator?.userAgent ?? "");
}

/**
 * A phone browser without a wallet: reopen this page in a wallet app's browser. `handoff` (a one-time
 * code from the server) lets that browser carry on as this scout instead of starting a new one.
 */
export function walletAppLink(chain: WalletChain, handoff?: string | null): string | null {
  const w = win();
  if (!w || !needsWalletApp()) return null;
  const u = new URL(w.location.href);
  u.searchParams.delete("handoff");
  if (handoff) u.searchParams.set("handoff", handoff);
  const url = u.toString();
  return chain === "solana"
    ? `https://phantom.app/ul/browse/${encodeURIComponent(url)}?ref=${encodeURIComponent(w.location.origin)}`
    : `https://metamask.app.link/dapp/${url.replace(/^https?:\/\//, "")}`;
}

/** Ask the wallet for its address (opens the wallet's connect prompt). */
export async function walletAddress(chain: WalletChain): Promise<string> {
  if (chain === "evm") {
    const p = evmProvider();
    if (!p) throw new Error("No Ethereum wallet found in this browser.");
    const list = (await p.request({ method: "eth_requestAccounts" })) as string[];
    if (!list?.[0]) throw new Error("The wallet shared no address.");
    return list[0];
  }
  const p = solanaProvider();
  if (!p) throw new Error("No Solana wallet found in this browser.");
  const res = await p.connect();
  const key = (res && "publicKey" in res ? res.publicKey : null) ?? p.publicKey;
  if (!key) throw new Error("The wallet shared no address.");
  return key.toBase58();
}

/** Sign a plain-text message (personal_sign / signMessage). Returns hex (EVM) or base58 (Solana). */
export async function walletSign(chain: WalletChain, address: string, message: string): Promise<string> {
  const bytes = new TextEncoder().encode(message);
  if (chain === "evm") {
    const p = evmProvider();
    if (!p) throw new Error("No Ethereum wallet found in this browser.");
    return String(await p.request({ method: "personal_sign", params: [`0x${bytesToHex(bytes)}`, address] }));
  }
  const p = solanaProvider();
  if (!p) throw new Error("No Solana wallet found in this browser.");
  const res = await p.signMessage(bytes, "utf8");
  const sig = res instanceof Uint8Array ? res : res.signature;
  return base58Encode(sig);
}

/** A wallet error as one short line for the toast. */
export function walletError(e: unknown): string {
  const err = e as { code?: number; message?: string };
  if (err?.code === 4001 || /reject|denied|cancel/i.test(err?.message ?? "")) return "Cancelled in the wallet. Nothing changed.";
  return (err?.message ?? "The wallet did not answer.").slice(0, 90);
}
